
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Search,
  RefreshCw,
  Calendar,
  FileDown
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface LeadsHeaderProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filter: {
    period: 'last7days' | 'last30days' | 'custom';
    startDate?: Date;
    endDate?: Date;
  };
  setFilter: (filter: {
    period: 'last7days' | 'last30days' | 'custom';
    startDate?: Date;
    endDate?: Date;
  }) => void;
  refresh: () => void;
  exportToExcel: () => void;
}

const LeadsHeader: React.FC<LeadsHeaderProps> = ({
  searchQuery,
  setSearchQuery,
  filter,
  setFilter,
  refresh,
  exportToExcel
}) => {
  const [startDate, setStartDate] = useState<Date | undefined>(filter.startDate);
  const [endDate, setEndDate] = useState<Date | undefined>(filter.endDate);
  
  const handlePeriodChange = (value: string) => {
    if (value === 'custom') {
      const today = new Date();
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      
      setStartDate(lastMonth);
      setEndDate(today);
      
      setFilter({
        period: 'custom',
        startDate: lastMonth,
        endDate: today
      });
    } else {
      setFilter({
        period: value as 'last7days' | 'last30days'
      });
    }
  };
  
  const handleStartDateSelect = (date: Date | undefined) => {
    setStartDate(date);
    if (date && endDate) {
      setFilter({
        period: 'custom',
        startDate: date,
        endDate
      });
    }
  };
  
  const handleEndDateSelect = (date: Date | undefined) => {
    setEndDate(date);
    if (startDate && date) {
      setFilter({
        period: 'custom',
        startDate,
        endDate: date
      });
    }
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow mb-6">
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div className="w-full md:w-1/2">
          <h1 className="text-2xl font-bold text-toctoc-black mb-2">Gestão de Leads</h1>
          <p className="text-gray-500">
            Visualize e gerencie todos os leads coletados pelo site
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={refresh}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button 
            className="bg-toctoc-orange hover:bg-orange-600 text-white"
            onClick={exportToExcel}
          >
            <FileDown className="w-4 h-4 mr-2" />
            Exportar Excel
          </Button>
        </div>
      </div>
      
      <div className="mt-6 flex flex-col md:flex-row gap-2">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Buscar por nome, email ou telefone..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Select
            defaultValue={filter.period}
            onValueChange={handlePeriodChange}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Selecione o período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last7days">Últimos 7 dias</SelectItem>
              <SelectItem value="last30days">Últimos 30 dias</SelectItem>
              <SelectItem value="custom">Período personalizado</SelectItem>
            </SelectContent>
          </Select>
          
          {filter.period === 'custom' && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[150px]">
                    <Calendar className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'dd/MM/yyyy', { locale: ptBR }) : 'Data inicial'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={startDate}
                    onSelect={handleStartDateSelect}
                    initialFocus
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[150px]">
                    <Calendar className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'dd/MM/yyyy', { locale: ptBR }) : 'Data final'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={endDate}
                    onSelect={handleEndDateSelect}
                    initialFocus
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadsHeader;
